import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
} from '@mui/material';
import { StyledOutlinedInput, StyledFormLabel, StyledSelect } from 'components/atoms';
import {
  CreateSelectMultipleFieldProps,
  withGridItemCSS,
  withWrapperCSS,
} from 'components/organisms/forms/form-builder/types';
import { noop, isNil, isArray, isEqual, isString } from 'lodash';
import React, { FC, useState, useRef } from 'react';
import { IoMdAdd } from 'react-icons/io';
import { MdSearch } from 'react-icons/md';
import { useCustomEffect } from 'utils/hooks/useCustomEffect';

import { MenuProps } from './';

type Key = string | number;

export const CreateSelectMultipleField: FC<
  CreateSelectMultipleFieldProps<Key[]> & withGridItemCSS & withWrapperCSS
> = ({
  disabled = false,
  error,
  fieldKey,
  gridItemCss,
  initValue = [],
  label = '',
  onAddOption,
  onChange = noop,
  options,
  labelCss,
  required = false,
  wrapperCss,
  xs = 12,
  multiple = true,
  readonly = false,
}) => {
  const strCmp = (a: string, b: string) => a.localeCompare(b);
  const byDescription = (a: { description: string }, b: { description: string }) =>
    strCmp(a.description, b.description);

  const normalizeInitValue = (initValue: Key | Key[]): Key[] => {
    initValue = isString(initValue) && initValue.length == 0 ? [] : initValue;
    return isArray(initValue) ? initValue : [initValue];
  };

  const [selected, setSelected] = useState<Key[]>(normalizeInitValue(initValue) || []);
  const [inputValue, setInputValue] = useState('');
  const [addError, setAddError] = useState(false);
  const [hasError, setHasError] = useState(error ? error : false);
  const [filteredOptions, setFilteredOptions] = useState(options.toSorted(byDescription));
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedValue = (selectedOptions: Key[]) => {
    if (multiple) {
      return selectedOptions;
    } else if (selectedOptions.length > 0) {
      return selectedOptions[0];
    }

    return '';
  };

  useCustomEffect(
    () => {
      onChange(selectedValue(selected), fieldKey);
    },
    [selected],
    (a, b) => isEqual(a, b),
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (newValue.trim() !== '') {
      setFilteredOptions(
        options.filter((option) =>
          option.description.toLowerCase().includes(newValue.toLowerCase()),
        ),
      );
    } else {
      setFilteredOptions(options.toSorted(byDescription));
    }
    // Re-focus the input field
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const selectChange = (event: SelectChangeEvent<Key[]>) => {
    let value: Key[];
    if (isNil(event.target.value)) {
      value = [];
    } else if (isArray(event.target.value)) {
      value = event.target.value;
    } else {
      value = [event.target.value];
    }

    const length = value.length;
    if (length === 0) setSelected([]);
    if (length > 0 && value[value.length - 1] !== undefined) {
      if (required && length === 0) setHasError(true);
      else setHasError(false);
      setSelected(value);
    }
  };

  const handleAddOption = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event) event.stopPropagation();
    if (inputValue.trim() !== '') {
      const newOption = {
        name: inputValue.trim(),
        description: inputValue.trim(),
      };
      onAddOption(inputValue)
        .then((data) => {
          options.push(newOption);
          setFilteredOptions(options.toSorted(byDescription));

          setSelected((prev) => {
            if (prev.length > 0) return [...prev, data.id];
            return [data.id];
          });

          setInputValue('');
        })
        .catch(() => setAddError(true));

      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Enter') handleAddOption();
  };

  const menuProps = { autoFocus: multiple, ...MenuProps };
  return (
    <Grid
      item
      xs={xs}
      style={{ ...gridItemCss, display: 'flex', alignItems: 'flex-end' }}
    >
      <FormControl
        error={!!hasError}
        required={required}
        style={{ width: '100%', ...wrapperCss }}
      >
        <StyledFormLabel error={!!hasError} style={labelCss}>
          {label} {required ? '*' : null}
        </StyledFormLabel>
        <StyledSelect
          data-testid={`select-${fieldKey}`}
          multiple={multiple}
          disabled={disabled}
          value={selectedValue(selected)}
          onChange={(event: any) => selectChange(event)}
          onOpen={() => {
            setTimeout(() => {
              inputRef.current?.focus();
            }, 0);
          }}
          MenuProps={menuProps}
          renderValue={() =>
            selected
              .map((value) => {
                const option = options.find((option) => option.name === value);
                return option?.description || '';
              })
              .toSorted(strCmp)
              .join(', ')
          }
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <StyledOutlinedInput
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              placeholder={readonly ? 'Search' : 'Search or add new item'}
              fullWidth
              startAdornment={
                readonly ? (
                  <InputAdornment position="start">
                    <MdSearch />
                  </InputAdornment>
                ) : null
              }
              endAdornment={
                !readonly ? (
                  <InputAdornment position="end">
                    <IconButton onClick={handleAddOption} edge="end">
                      <IoMdAdd />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          </Box>
          {filteredOptions.map((option) => (
            <MenuItem key={`${fieldKey}-option-${option.name}`} value={option.name}>
              <Checkbox
                style={{ display: !multiple ? 'none' : 'block' }}
                checked={selected.includes(option.name)}
              />
              <ListItemText primary={option.description} />
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setAddError(false)}
        open={addError}
      >
        <Alert onClose={() => setAddError(false)} severity="error" variant="filled">
          Failed to create Exercise. Resolve form issues and try again.
        </Alert>
      </Snackbar>
    </Grid>
  );
};
